/**
 * Seitenübersicht (/seitenuebersicht): alle öffentlichen Inhaltsseiten als
 * einfache Linkliste, serverseitig gerendert.
 *
 * Ahrefs meldete am 07.10.2026 39 verwaiste Seiten (Blog, Cases, Keynotes,
 * Freebies): Blogliste, Case-Karussell und Teile des Menüs laden ihre Links
 * erst im Browser, Crawler sehen sie nicht. Diese Seite gibt jeder
 * öffentlichen Seite einen festen internen Link.
 *
 * Bewusst NICHT enthalten: die Themenseiten unter /themen/ (KI-Texte für
 * Crawler). Sie sollen auf der Website nirgends verlinkt sein.
 */
import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

export type SiteOverviewLink = { to: string; label: string };

export type SiteOverview = {
  blog: SiteOverviewLink[];
  cases: SiteOverviewLink[];
  downloads: SiteOverviewLink[];
  /** Offene Funktionsschalter, damit das Menü wie im Header gefiltert wird. */
  enabledFlags: string[];
};

export const getSiteOverview = createServerFn({ method: "GET" }).handler(
  async (): Promise<SiteOverview> => {
    const [flags, blog, cases, leadMagnets] = await Promise.all([
      supabaseAdmin.from("feature_flags").select("key").eq("enabled", true),
      supabaseAdmin
        .from("blog_posts")
        .select("slug, title")
        .eq("published", true)
        .order("title", { ascending: true }),
      supabaseAdmin
        .from("cases")
        .select("slug, title, client_name")
        .eq("published", true)
        .order("title", { ascending: true }),
      supabaseAdmin
        .from("lead_magnets")
        .select("slug, title")
        .eq("status", "veroeffentlicht")
        .order("title", { ascending: true }),
    ]);
    if (blog.error) throw new Error(blog.error.message);
    if (cases.error) throw new Error(cases.error.message);
    if (leadMagnets.error) throw new Error(leadMagnets.error.message);

    return {
      enabledFlags: (flags.data ?? []).map((r) => r.key),
      blog: (blog.data ?? []).map((r) => ({ to: `/blog/${r.slug}`, label: r.title })),
      cases: (cases.data ?? []).map((r) => ({
        to: `/cases/${r.slug}`,
        label: r.client_name ? `${r.client_name}: ${r.title}` : r.title,
      })),
      downloads: (leadMagnets.data ?? []).map((r) => ({
        to: `/kostenlos/${r.slug}`,
        label: r.title,
      })),
    };
  },
);
